<template>
  <div v-if="request" class="px-4 pb-4">
    <h3 class="text-base mt-2">{{ request.title }}</h3>

    <div v-if="requestDetail" class="rev-request-grid mt-4">
      <div class="font-600">{{ $t('rev_requests.status') }}:</div>
      <div>
        <div class="rev-request-status">
          <span :class="[`rev-request-status-circle rev-request-status--${requestDetail.status}`]"
            >&nbsp;</span
          >
          {{ $t(`requests.statuses.${requestDetail.status}`) }}
        </div>
      </div>

      <div class="font-600">{{ $t('rev_requests.facility_name') }}:</div>
      <div>{{ facilityName }}</div>

      <div class="font-600 flex items-center">{{ $t('rev_requests.customer') }}:</div>
      <div>
        <template v-if="isViewMode">{{ requestDetail.reservation_name }}</template>
        <t-input v-else v-model="form.reservation_name"></t-input>
      </div>

      <div class="font-600">{{ $t('rev_requests.requested_day') }}:</div>
      <div>{{ requestDate }}</div>

      <div class="font-600">{{ $t('rev_requests.requested_time') }}:</div>
      <div>{{ requestTime }}</div>

      <div class="font-600">{{ $t('rev_requests.people') }}:</div>
      <div>{{ requestDetail.number_of_persons }}</div>

      <div class="font-600 flex items-center">{{ $t('base.email') }}:</div>
      <div>
        <template v-if="isViewMode">{{ requestDetail.email }}</template>
        <t-input v-else v-model="form.email"></t-input>
      </div>

      <div class="font-600 flex items-center">{{ $t('rev_requests.phone') }}:</div>
      <div>
        <template v-if="isViewMode">{{ requestDetail.phone_number }}</template>
        <t-input v-else v-model="form.phone_number"></t-input>
      </div>

      <template v-for="(value, label, index) in customFields" v-if="customFields">
        <div :key="`label-${index}`" class="font-600">{{ label }}:</div>
        <div :key="`value-${index}`">{{ value }}</div>
      </template>

      <div class="font-600">{{ $t('rev_requests.payment') }}:</div>
      <div>{{ requestDetail.total_price }} {{ $t('currencies.JPY') }}</div>
    </div>

    <div v-if="!loading && canEdit" class="flex mt-4">
      <template v-if="isViewMode">
        <theme-button class="w-1/2" outline @click="showCancelModal = true">{{
          $t('rev_requests.cancel_request')
        }}</theme-button>
        <theme-button class="w-1/2 ml-2" @click="onEditClick">{{
          $t('rev_requests.edit_request')
        }}</theme-button>
      </template>
      <template v-else>
        <theme-button :disabled="isSaving" class="w-1/2" outline @click="mode = 'view'">{{
          $t('base.cancel')
        }}</theme-button>
        <theme-button :loading="isSaving" class="w-1/2 ml-2" @click="handleSave">{{
          $t('base.save')
        }}</theme-button>
      </template>
    </div>

    <div v-if="loading" class="text-center mt-4">
      <t-spinner v-theme-font></t-spinner>
    </div>

    <t-modal transition="slide-up-fade" :visible.sync="showCancelModal">
      <template #header>
        <h3>{{ $t('rev_requests.cancel_request') }}</h3>
      </template>

      <div class="p-4">
        <p>{{ $t('rev_requests.confirm_cancel_msg') }}</p>

        <div class="flex items-center mt-4">
          <theme-button
            class="w-1/2"
            :disabled="isCancelling"
            outline
            @click="showCancelModal = false"
            >{{ $t('rev_requests.keep_request') }}</theme-button
          >
          <theme-button class="w-1/2 ml-2" :disabled="isCancelling" @click="handleCancel">{{
            $t('rev_requests.cancel_request')
          }}</theme-button>
        </div>
      </div>
    </t-modal>
  </div>
</template>

<script>
import ThemeButton from 'javascript/Common/ThemeButton'
import { UserReservations } from 'src/apis'
import { findCurrentTranslation } from 'Utilities/translations'
import dayjs from 'dayjs'
import TInput from 'ChatWidget/Common/TInput'

export default {
  name: 'RequestShow',
  components: { TInput, ThemeButton },
  props: {
    request: { type: Object, default: null }
  },

  data() {
    return {
      loading: false,
      isSaving: false,
      isCancelling: false,
      requestDetail: null,
      showCancelModal: false,
      form: {
        email: '',
        phone_number: ''
      },
      mode: 'view'
    }
  },

  computed: {
    canEdit() {
      return !['cancelled', 'cancel_requested', 'done', '_destroyed', 'no_show'].includes(
        this.requestDetail?.status
      )
    },

    isViewMode() {
      return this.mode === 'view'
    },

    facilityName() {
      return this.requestDetail
        ? findCurrentTranslation(
            this.requestDetail.hotel.translations_attributes,
            this.$i18n.locale
          )
        : ''
    },

    requestDate() {
      return this.requestDetail?.started_at
        ? dayjs(this.requestDetail.started_at).tz().format('MM/DD/YYYY')
        : ''
    },

    requestTime() {
      return this.requestDetail?.started_at
        ? dayjs(this.requestDetail.started_at).tz().format('HH:mm')
        : ''
    },

    customFields() {
      if (!this.requestDetail?.customized_request) return null

      return Object.keys(this.requestDetail.customized_request).length > 0
        ? this.requestDetail.customized_request
        : null
    }
  },

  created() {
    this.loading = true
    UserReservations.getReservation(this.request.id)
      .then((request) => {
        this.requestDetail = request
      })
      .finally(() => {
        this.loading = false
      })
  },

  methods: {
    handleSave() {
      this.isSaving = true
      UserReservations.updateReservation(this.request.id, this.form)
        .then((request) => {
          this.mode = 'view'
          this.requestDetail = request
          this.$emit('refresh')
        })
        .finally(() => {
          this.isSaving = false
        })
    },

    handleCancel() {
      this.isCancelling = true
      UserReservations.cancelReservation(this.request.id)
        .then((request) => {
          this.requestDetail = request
          this.showCancelModal = false
          this.$emit('refresh')
        })
        .finally(() => {
          this.isCancelling = false
        })
    },

    onEditClick() {
      this.mode = 'edit'
      this.form = JSON.parse(JSON.stringify(this.requestDetail))
    }
  }
}
</script>
